import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, Shield, BarChart, Users, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import { safeStorage } from '../utils/safeStorage';

interface CookieCategory {
  id: string;
  required: boolean;
  enabled: boolean;
}

const CookieSettings: React.FC = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<Record<string, CookieCategory>>({
    essential: {
      id: 'essential',
      required: true,
      enabled: true,
    },
    analytics: {
      id: 'analytics',
      required: false,
      enabled: false,
    },
    marketing: {
      id: 'marketing',
      required: false,
      enabled: false,
    },
    functional: {
      id: 'functional',
      required: false,
      enabled: false,
    },
  });

  useEffect(() => {
    // Load current preferences from localStorage
    const savedPreferences = safeStorage.getJSON<Record<string, CookieCategory>>('cookiePreferences');
    if (savedPreferences) {
      setCategories(prev => ({
        ...prev,
        ...savedPreferences,
      }));
    }
  }, []);

  const updateCategory = (categoryId: string, enabled: boolean) => {
    setCategories(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        enabled,
      },
    }));
  };

  const savePreferences = () => {
    safeStorage.setJSON('cookiePreferences', categories);
    safeStorage.setItem('cookiesAccepted', 'custom');
    safeStorage.setItem('cookiesAcceptedDate', new Date().toISOString());

    // You could also dispatch an event here to notify other components
    window.dispatchEvent(new CustomEvent('cookiePreferencesUpdated', {
      detail: categories
    }));
  };

  const acceptAll = () => {
    const allEnabled = Object.keys(categories).reduce((acc, key) => ({
      ...acc,
      [key]: { ...categories[key], enabled: true },
    }), {});

    setCategories(allEnabled);
    safeStorage.setJSON('cookiePreferences', allEnabled);
    safeStorage.setItem('cookiesAccepted', 'all');
    safeStorage.setItem('cookiesAcceptedDate', new Date().toISOString());
  };

  const acceptEssentialOnly = () => {
    const essentialOnly = Object.keys(categories).reduce((acc, key) => ({
      ...acc,
      [key]: {
        ...categories[key],
        enabled: categories[key].required
      },
    }), {});

    setCategories(essentialOnly);
    safeStorage.setJSON('cookiePreferences', essentialOnly);
    safeStorage.setItem('cookiesAccepted', 'essential');
    safeStorage.setItem('cookiesAcceptedDate', new Date().toISOString());
  };

  return (
    <AppLayout>
      <div className="flex-1 overflow-auto bg-gradient-to-br from-primary-500 to-secondary-500">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Cookie className="w-12 h-12 text-primary-500" />
                <h1 className="text-4xl font-bold text-gray-900">
                  {t('cookieSettings.title')}
                </h1>
              </div>
              <p className="text-gray-600 text-lg">
                {t('cookieSettings.description')}
              </p>
            </div>

            <div className="space-y-8">
              {/* Essential Cookies */}
              <div className="border border-gray-200 rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-green-500" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {t('cookieSettings.categories.essential.title')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t('cookieSettings.categories.essential.description')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500 mr-3">
                      {t('cookieSettings.alwaysActive')}
                    </span>
                    <div className="w-12 h-6 bg-green-500 rounded-full relative">
                      <div className="w-5 h-5 bg-white rounded-full absolute top-0.5 right-0.5"></div>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {t('cookieSettings.categories.essential.details')}
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="border border-gray-200 rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <BarChart className="w-6 h-6 text-blue-500" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {t('cookieSettings.categories.analytics.title')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t('cookieSettings.categories.analytics.description')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => updateCategory('analytics', !categories.analytics.enabled)}
                    className={`w-12 h-6 rounded-full relative transition-colors duration-200 ${categories.analytics.enabled ? 'bg-primary-500' : 'bg-gray-300'
                      }`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform duration-200 ${categories.analytics.enabled ? 'translate-x-6' : 'translate-x-0.5'
                        }`}
                    ></div>
                  </button>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {t('cookieSettings.categories.analytics.details')}
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="border border-gray-200 rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Users className="w-6 h-6 text-purple-500" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {t('cookieSettings.categories.marketing.title')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t('cookieSettings.categories.marketing.description')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => updateCategory('marketing', !categories.marketing.enabled)}
                    className={`w-12 h-6 rounded-full relative transition-colors duration-200 ${categories.marketing.enabled ? 'bg-primary-500' : 'bg-gray-300'
                      }`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform duration-200 ${categories.marketing.enabled ? 'translate-x-6' : 'translate-x-0.5'
                        }`}
                    ></div>
                  </button>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {t('cookieSettings.categories.marketing.details')}
                </p>
              </div>

              {/* Functional Cookies */}
              <div className="border border-gray-200 rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Cookie className="w-6 h-6 text-orange-500" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {t('cookieSettings.categories.functional.title')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t('cookieSettings.categories.functional.description')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => updateCategory('functional', !categories.functional.enabled)}
                    className={`w-12 h-6 rounded-full relative transition-colors duration-200 ${categories.functional.enabled ? 'bg-primary-500' : 'bg-gray-300'
                      }`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform duration-200 ${categories.functional.enabled ? 'translate-x-6' : 'translate-x-0.5'
                        }`}
                    ></div>
                  </button>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {t('cookieSettings.categories.functional.details')}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-12 pt-8 border-t border-gray-200">
              <button
                onClick={acceptEssentialOnly}
                className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium transition-colors duration-200"
              >
                {t('cookieSettings.actions.essentialOnly')}
              </button>
              <button
                onClick={savePreferences}
                className="flex-1 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors duration-200"
              >
                {t('cookieSettings.actions.savePreferences')}
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                {t('cookieSettings.actions.acceptAll')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default CookieSettings;
